import { FC, useState, useRef, useEffect } from 'react';
import { Card } from '../ui/card';
import { VideoPlayer } from './video/VideoPlayer';
import { useLessonProgress } from '@/hooks/useLessonProgress';

interface LessonMediaProps {
  videoUrl: string;
  audioUrl: string;
  contentText: string;
  lessonId: string;
}

export const LessonMedia: FC<LessonMediaProps> = ({ 
  videoUrl, 
  audioUrl, 
  contentText, 
  lessonId 
}) => {
  const [hasReadContent, setHasReadContent] = useState(false);
  const [audioCompleted, setAudioCompleted] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const readingTimeoutRef = useRef<NodeJS.Timeout>();
  const { updateProgress } = useLessonProgress(lessonId);

  // Función para normalizar URLs
  const getFullUrl = (url: string | undefined) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `https://${url.replace(/^\/+/, '')}`;
  };

  const fullVideoUrl = getFullUrl(videoUrl);
  const fullAudioUrl = getFullUrl(audioUrl);

  // Función para actualizar el progreso
  const updateMediaProgress = async (type: 'video' | 'audio' | 'reading') => {
    try {
      await updateProgress.mutateAsync({
        type,
        completed: true,
        completed_at: new Date().toISOString()
      });

      // Actualizar estado local
      if (type === 'audio') setAudioCompleted(true);
      else if (type === 'video') setVideoCompleted(true);
      else if (type === 'reading') setHasReadContent(true);

    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // Rastrear la lectura del contenido
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      if (hasReadContent) return;

      const { scrollTop, scrollHeight, clientHeight } = contentElement;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.8) {
        if (readingTimeoutRef.current) {
          clearTimeout(readingTimeoutRef.current);
        }

        readingTimeoutRef.current = setTimeout(() => {
          updateMediaProgress('reading');
        }, 10000); // 10 segundos de lectura
      }
    };

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      if (readingTimeoutRef.current) {
        clearTimeout(readingTimeoutRef.current);
      }
    };
  }, [hasReadContent]);

  // Manejar el progreso del video
  const handleVideoComplete = () => {
    if (!videoCompleted) {
      updateMediaProgress('video');
    }
  };

  const handleVideoProgress = (progress: number) => {
    console.log('Progreso del video:', progress);
  };

  // Manejar el progreso del audio
  const handleAudioTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    const progress = (audio.currentTime / audio.duration) * 100;
    setAudioProgress(progress);
    
    // Marcar como completado cuando se alcance el 90% y no esté ya completado
    if (progress >= 90 && !audioCompleted) {
      updateMediaProgress('audio');
    }
  };

  return (
    <div className="space-y-8">
      {videoUrl && (
        <div className="aspect-video rounded-lg overflow-hidden bg-black">
          <VideoPlayer
            url={fullVideoUrl}
            lessonId={lessonId}
            onVideoComplete={handleVideoComplete}
            onVideoProgress={handleVideoProgress}
          />
        </div>
      )}

      {audioUrl && (
        <div className="w-full">
          <h2 className="text-xl font-semibold mb-4 text-primary">Material de audio complementario</h2>
          <audio
            ref={audioRef}
            className="w-full"
            controls
            onTimeUpdate={handleAudioTimeUpdate}
            preload="metadata"
            onError={(e) => {
              console.error('Error loading audio:', {
                error: e,
                audioError: audioRef.current?.error,
                url: fullAudioUrl,
                originalUrl: audioUrl
              });
            }}
          >
            <source src={fullAudioUrl} type="audio/mpeg" />
            Tu navegador no soporta el elemento de audio.
          </audio>
          {audioProgress > 0 && !audioCompleted && (
            <p className="text-sm text-muted-foreground mt-2">
              Progreso: {Math.round(audioProgress)}%
            </p>
          )}
        </div>
      )}

      {contentText && (
        <div className="w-full">
          <h2 className="text-xl font-semibold mb-4 text-primary">Material de lectura</h2>
          <div 
            ref={contentRef}
            className="prose prose-blue max-w-none overflow-y-auto max-h-96 p-4 bg-white rounded-lg border border-gray-200"
            dangerouslySetInnerHTML={{ __html: contentText }}
          />
        </div>
      )}
    </div>
  );
};